/**
 * Employee Sidebar (DC-182, moved + adapted for DC-1648).
 *
 * Draggable employee list with search, assignment-status filtering, and
 * drag-and-drop support for assigning employees to persona nodes in the
 * FAIR persona topology. Role-tag colors come from scoped CSS classes
 * (persona-topology.css tokens), not inline hex.
 */

import React, { useState } from 'react';
import type { TopologyEmployee, EmployeeAssignmentInfo } from './types';
import { roleClassOf } from './types';

interface EmployeeSidebarProps {
  employees: TopologyEmployee[];
  loading: boolean;
  onDragStart: (employee: TopologyEmployee, event: React.DragEvent) => void;
  employeeAssignments: Map<string, EmployeeAssignmentInfo>;
  onUnassign: (employeeId: string, personaId: string, personaName: string) => void;
}

export const EmployeeSidebar: React.FC<EmployeeSidebarProps> = ({
  employees,
  loading,
  onDragStart,
  employeeAssignments,
  onUnassign,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'assigned' | 'unassigned'>('all');

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.department?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    const hasAssignments = employeeAssignments.has(emp.employee_id);
    if (filterStatus === 'assigned' && !hasAssignments) return false;
    if (filterStatus === 'unassigned' && hasAssignments) return false;

    return true;
  });

  const assignedCount = employees.filter(e => employeeAssignments.has(e.employee_id)).length;
  const unassignedCount = employees.length - assignedCount;

  if (isCollapsed) {
    return (
      <div className="employee-sidebar collapsed">
        <button
          className="sidebar-toggle"
          onClick={() => setIsCollapsed(false)}
          title="Expand team panel"
        >
          <span className="toggle-icon">&#x25B6;</span>
          <span className="toggle-label">Team</span>
        </button>
      </div>
    );
  }

  return (
    <div className="employee-sidebar">
      <div className="sidebar-header">
        <h4>Team Members</h4>
        <button
          className="sidebar-collapse"
          onClick={() => setIsCollapsed(true)}
          title="Collapse panel"
        >
          &#x25C0;
        </button>
      </div>

      <div className="sidebar-search">
        <input
          type="text"
          placeholder="Search team..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="sidebar-filter-tabs">
        <button
          className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          All ({employees.length})
        </button>
        <button
          className={`filter-tab ${filterStatus === 'assigned' ? 'active' : ''}`}
          onClick={() => setFilterStatus('assigned')}
        >
          Assigned ({assignedCount})
        </button>
        <button
          className={`filter-tab ${filterStatus === 'unassigned' ? 'active' : ''}`}
          onClick={() => setFilterStatus('unassigned')}
        >
          Unassigned ({unassignedCount})
        </button>
      </div>

      <div className="sidebar-hint">
        <span>Drag a team member onto a persona to assign</span>
      </div>

      {loading ? (
        <div className="sidebar-loading">Loading team...</div>
      ) : employees.length === 0 ? (
        <div className="sidebar-empty">
          <p>No team members yet</p>
          <a href="/settings/team" className="link-add-team">
            Add team members
          </a>
        </div>
      ) : (
        <div className="employee-list">
          {filteredEmployees.map((emp) => {
            const assignmentInfo = employeeAssignments.get(emp.employee_id);
            const isAssigned = !!assignmentInfo && assignmentInfo.assignments.length > 0;

            return (
              <div
                key={emp.employee_id}
                className={`employee-card draggable ${isAssigned ? 'has-assignments' : ''}`}
                draggable
                onDragStart={(e) => onDragStart(emp, e)}
              >
                <div className="employee-avatar">
                  {emp.display_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="employee-info">
                  <span className="employee-name">{emp.display_name}</span>

                  {isAssigned ? (
                    <div className="assignment-status assigned">
                      <span className="status-badge assigned">
                        Assigned ({assignmentInfo.assignments.length})
                      </span>
                      <div className="assigned-roles">
                        {assignmentInfo.assignments.slice(0, 2).map((a, idx) => (
                          <span
                            key={idx}
                            className={`role-tag ${roleClassOf(a.primary_role)}`}
                            title={`${a.persona_name} - Click X to unassign`}
                          >
                            {a.persona_name.length > 12
                              ? a.persona_name.slice(0, 11) + '...'
                              : a.persona_name}
                            <button
                              className="unassign-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                onUnassign(emp.employee_id, a.persona_id, a.persona_name);
                              }}
                              title={`Remove ${a.persona_name} assignment`}
                              aria-label={`Remove ${a.persona_name} assignment from ${emp.display_name}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {assignmentInfo.assignments.length > 2 && (
                          <span className="more-roles">
                            +{assignmentInfo.assignments.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="assignment-status unassigned">
                      <span className="status-badge unassigned">Unassigned</span>
                    </div>
                  )}

                  {emp.department && (
                    <span className="employee-dept">{emp.department}</span>
                  )}
                </div>
              </div>
            );
          })}
          {filteredEmployees.length === 0 && (searchTerm || filterStatus !== 'all') && (
            <div className="no-results">
              {searchTerm ? 'No matching team members' : `No ${filterStatus} team members`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
